import React from 'react';
import clsx from 'clsx';
import {ErrorField} from '@netx/core-components/ErrorField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {StyledTextField, type StyledTextFieldProps} from './StyledTextField';
import {
	getTextFieldUtilityClass,
	textFieldClasses,
	type TextFieldClasses,
} from './textFieldClasses';

type TextFieldOwnerState = WithClasses<TextFieldClasses> & {
	fullWidth?: boolean;
};

const useUtilityClasses = (ownerState: TextFieldOwnerState) => {
	const {classes, fullWidth} = ownerState;
	const slots = {
		root: ['root', fullWidth && 'fullWidth'],
	};
	return composeClasses(slots, getTextFieldUtilityClass, classes);
};

const TextFieldRoot = styled('div', {
	name: 'TextField',
	slot: 'Root',
	overridesResolver: (props, styles) => styles.root,
})(({theme, ownerState}: WithOwnerState<TextFieldOwnerState>) => ({
	display: 'inline-flex',
	flexDirection: 'column',
	width: ownerState?.fullWidth ? '100%' : undefined,
	'& .MuiFormHelperText-root': {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
}));

export interface TextFieldProps
	extends Omit<StyledTextFieldProps, 'classes' | 'error' | 'onChange' | 'sx'>,
		WithClasses<TextFieldClasses> {
	error?: string;
	hideErrorMessage?: boolean;
	loading?: boolean;
	onChange?: (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		value: string,
	) => void;
}

export const TextFieldPrimitive = (
	{
		classes: _classes,
		className,
		disabled,
		error,
		fullWidth,
		hideErrorMessage,
		style,
		sx,
		value,
		onChange,
		...props
	}: TextFieldProps,
	ref: any,
) => {
	value = value == null ? '' : value;

	const ownerState: TextFieldOwnerState = {
		classes: _classes,
		className,
		fullWidth,
	};

	const classes = useUtilityClasses(ownerState);

	const handleChange = useConstCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Sometimes a change event will come through that was not related to
			// interacting directly with the input
			// I cannot seem to recreate this in the tests yet, but this is an
			// important chack
			if (!event) return;
			onChange?.(event, event.target.value);
		},
	);

	return (
		<TextFieldRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			style={style}
			sx={sx}
		>
			<StyledTextField
				ref={ref}
				{...props}
				disabled={disabled}
				error={!!error}
				value={value}
				onChange={handleChange}
			/>
			{!hideErrorMessage ? (
				<ErrorField disabled={disabled} error={error} />
			) : null}
		</TextFieldRoot>
	);
};

export const TextField = React.forwardRef(TextFieldPrimitive) as (
	props: TextFieldProps,
) => JSX.Element;
export default TextField;
